import type { Column, Row } from '@tanstack/react-table';

import type { DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';

import type { DsAnyRow } from 'src/component/ds/dataTable/types';

/* Per-cell rules: which cells are blank, and which drop their bottom border. */

type TRow<TData extends DsAnyRow> = Row<DataTableFeatures, TData>;
type TColumn<TData extends DsAnyRow> = Column<DataTableFeatures, TData, unknown>;

/*
 * True when the column shows on this row's level (`meta.levels`, unset for
 * every level). A missing value on a level it shows is just a missing value.
 */
const columnAppliesTo = <TData extends DsAnyRow>(column: TColumn<TData>, row: TRow<TData>) => {
  const levels = column.columnDef.meta?.levels as readonly string[] | undefined;
  return !levels || levels.includes(row.original.level);
};

/* The row before this one under the same parent (or the previous top-level row). */
const previousSibling = <TData extends DsAnyRow>(row: TRow<TData>): TRow<TData> | undefined =>
  (row.getParentRow()?.subRows ?? row.table.getRowModel().rows)[row.index - 1];

/*
 * True when a grouping (`hideRepeats`) cell has the same value as the row
 * above, and so do all grouping columns to its left -- a panel tag that
 * happens to match still shows under a new area.
 */
function repeatsRowAbove<TData extends DsAnyRow> (
  column: TColumn<TData>,
  row: TRow<TData>,
  groupingColumns: TColumn<TData>[]
) {
  const index = groupingColumns.indexOf(column);
  const above = previousSibling(row);
  if (index < 0 || !above) return false;
  return groupingColumns
    .slice(0, index + 1)
    .every((c) => above.getValue(c.id) === row.getValue(c.id));
}

/* True when the cell should render nothing. */
export const isBlankCell = <TData extends DsAnyRow>(
  column: TColumn<TData>,
  row: TRow<TData>,
  groupingColumns: TColumn<TData>[]
) => !columnAppliesTo(column, row) || repeatsRowAbove(column, row, groupingColumns);

/*
 * True when a blank cell is part of the filled cell above it: a repeated
 * area/panel, or a child row under its parent's column (Controller on a
 * circuit). A plain missing value isn't.
 */
const extendsCellAbove = <TData extends DsAnyRow>(
  column: TColumn<TData>,
  row: TRow<TData>,
  groupingColumns: TColumn<TData>[]
) => (!columnAppliesTo(column, row) && row.depth > 0) || repeatsRowAbove(column, row, groupingColumns);

/*
 * True when the cell below extends this one's group, so the line between them
 * goes and the group -- filled cell included -- reads as one tall cell.
 */
export const hidesBottomBorder = <TData extends DsAnyRow>(
  column: TColumn<TData>,
  row: TRow<TData>,
  rowBelow: TRow<TData> | undefined,
  groupingColumns: TColumn<TData>[]
) => !!rowBelow && extendsCellAbove(column, rowBelow, groupingColumns);
